/**
=========================================================
* EEG Question Component
* Question with animated EEG signal graph
=========================================================
*/

import { useEffect, useState, useRef } from "react";

// react-chartjs-2 components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

// @mui material components
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Dark background color for medical device
const deviceBackground = "#0a0a0a";
const SWEEP_DURATION_SECONDS = 10;
const SAMPLING_RATE = 100; // Samples per second
const TOTAL_SAMPLES = SWEEP_DURATION_SECONDS * SAMPLING_RATE; // 1000 samples for 10 seconds

function EEGQuestion({ question, correctState = "awake", isMultipleChoice = false, options = null }) {
  const [waveformData, setWaveformData] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(isMultipleChoice ? [] : ""); // User's answer selection
  const correctStateRef = useRef(correctState); // Ref to track which signal to display
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const isInitialMount = useRef(true);

  // Generate EEG signal based on correctState (the signal to display)
  const generateEEGSignal = (t, elapsed) => {
    const currentState = correctStateRef.current;
    switch (currentState) {
      case "awake":
        return generateAwakeSignal(t);
      case "goestosleep":
        return generateAnesthesiaInductionSignal(t, elapsed);
      case "burstsupression":
        return generateBurstSuppressionSignal(t, elapsed);
      case "eyeblink":
        return generateEyeBlinkSignal(t, elapsed);
      default:
        return generateAwakeSignal(t);
    }
  };

  // Seeded random function for deterministic noise based on time
  const seededRandom = (seed) => {
    // Simple hash function to generate pseudo-random value from seed
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };

  // Awake state: Beta prominent (20-24 Hz), some alpha, minimal theta/delta
  const generateAwakeSignal = (t) => {
    const deltaAmp = 5; // Minimal delta
    const thetaAmp = 8; // Minimal theta
    const alphaAmp = 20; // Moderate alpha
    const betaAmp = 25; // Prominent beta (frontal)
    
    const delta = deltaAmp * Math.sin(2 * Math.PI * 2.5 * t);
    const theta = thetaAmp * Math.sin(2 * Math.PI * 6 * t + Math.PI / 4);
    const alpha = alphaAmp * Math.sin(2 * Math.PI * 10 * t + Math.PI / 2);
    const beta1 = betaAmp * Math.sin(2 * Math.PI * 20 * t);
    const beta2 = betaAmp * 0.8 * Math.sin(2 * Math.PI * 24 * t + Math.PI / 3);
    
    // Add deterministic noise based on time position (not random, so already displayed values stay fixed)
    // Use time * sampling rate to get unique seed for each sample point
    const noiseSeed = t * SAMPLING_RATE;
    const noise = (seededRandom(noiseSeed) - 0.5) * 60; // ±1.5 µV
    
    return delta + theta + alpha + beta1 + beta2 + noise;
  };

  // Anesthesia induction: High frequencies decrease, alpha becomes more prominent
  const generateAnesthesiaInductionSignal = (t, elapsed) => {
    // Progress from awake to asleep based on the point's own time position (t)
    // This ensures each point is calculated based on its position, not current elapsed time
    const progress = Math.min(t / SWEEP_DURATION_SECONDS, 1);
    
    // Alpha frequency slows down (10 Hz -> 8 Hz)
    const alphaFreq = 10 - (2 * progress);
    // Alpha amplitude increases
    const alphaAmp = 20 + (30 * progress);
    
    // Beta amplitude decreases
    const betaAmp = 25 * (1 - progress * 0.8);
    
    // Theta and delta increase slightly
    const thetaAmp = 8 + (10 * progress);
    const deltaAmp = 5 + (8 * progress);
    
    const delta = deltaAmp * Math.sin(2 * Math.PI * 2.5 * t);
    const theta = thetaAmp * Math.sin(2 * Math.PI * 6 * t + Math.PI / 4);
    const alpha = alphaAmp * Math.sin(2 * Math.PI * alphaFreq * t + Math.PI / 2);
    const beta1 = betaAmp * Math.sin(2 * Math.PI * 20 * t);
    const beta2 = betaAmp * 0.8 * Math.sin(2 * Math.PI * 24 * t + Math.PI / 3);
    
    // Add deterministic noise based on time position (not random, so already displayed values stay fixed)
    const noiseSeed = t * SAMPLING_RATE;
    const noise = (seededRandom(noiseSeed) - 0.5) * 40; // ±1.5 µV
    
    return delta + theta + alpha + beta1 + beta2 + noise;
  };

  // Burst suppression: Alternating bursts and suppression periods
  const generateBurstSuppressionSignal = (t, elapsed) => {
    const cycleTime = t % 5; // 5 second cycles (allows 2 bursts in 10 seconds)
    const isBurst = cycleTime < 2; // Burst for 2s, suppression for 3s
    
    if (!isBurst) {
      // Suppression: near-zero signal with minimal deterministic noise
      const noiseSeed = t * SAMPLING_RATE;
      const suppressionNoise = (seededRandom(noiseSeed) - 0.5) * 20; // Very small noise during suppression
      return suppressionNoise;
    }
    
    // Burst: high-amplitude mixed frequencies with noise (like coma state)
    const delta = 30 * Math.sin(2 * Math.PI * 2 * t);
    const theta = 25 * Math.sin(2 * Math.PI * 5 * t + Math.PI / 4);
    const alpha = 35 * Math.sin(2 * Math.PI * 8 * t + Math.PI / 2);
    const beta = 20 * Math.sin(2 * Math.PI * 15 * t);
    
    // Add deterministic noise to bursts to make them irregular and noisy (like the image)
    const noiseSeed = t * SAMPLING_RATE;
    const burstNoise = (seededRandom(noiseSeed) - 0.5) * 60; // Same noise level as goestosleep
    
    return delta + theta + alpha + beta + burstNoise;
  };

  // Eye blink: Positive deflections with rapid amplitude changes
  const generateEyeBlinkSignal = (t, elapsed) => {
    // Base awake signal (already includes noise)
    const baseSignal = generateAwakeSignal(t);
    
    // Blink artifact appears at specific times: 3.9s and 7.9s
    const blinkTimes = [2.9, 7.9];
    const blinkDuration = 0.2; // Blink lasts 0.3 seconds
    const blinkPeakAmplitude = 150; // Increased peak amplitude (was 110)
    
    let blinkArtifact = 0;
    
    // Check if we're within a blink period
    for (const blinkStartTime of blinkTimes) {
      const timeSinceBlink = t - blinkStartTime;
      if (timeSinceBlink >= 0 && timeSinceBlink < blinkDuration) {
        // Positive deflection: rapid rise then fall
        if (timeSinceBlink < 0.15) {
          // Rise phase: 0 to peak
          blinkArtifact = blinkPeakAmplitude * (timeSinceBlink / 0.15);
        } else {
          // Fall phase: peak to 0
          blinkArtifact = blinkPeakAmplitude * (1 - (timeSinceBlink - 0.15) / 0.15);
        }
        break; // Only one blink at a time
      }
    }
    
    return baseSignal + blinkArtifact;
  };

  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Reset waveform data and start time when component mounts or correctState changes
    setWaveformData([]);
    startTimeRef.current = Date.now();
    correctStateRef.current = correctState;

    const generateEEGWave = () => {
      // Check if component is still mounted (animationFrameRef will be null if cancelled)
      if (!animationFrameRef.current) {
        return;
      }

      const now = Date.now();
      let elapsed = (now - startTimeRef.current) / 1000; // Elapsed time in seconds
      
      // Reset after 10 seconds
      if (elapsed >= SWEEP_DURATION_SECONDS) {
        startTimeRef.current = now;
        elapsed = 0; // Reset elapsed time
      }

      // Generate data points from 0 to elapsed time
      const dataPoints = [];
      
      // Generate samples up to current elapsed time
      for (let t = 0; t <= elapsed; t += 1 / SAMPLING_RATE) {
        const value = generateEEGSignal(t, elapsed);
        dataPoints.push({ x: t, y: value });
      }

      setWaveformData(dataPoints);

      // Continue animation only if still mounted
      if (animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(generateEEGWave);
      }
    };
    
    // Start animation
    animationFrameRef.current = requestAnimationFrame(generateEEGWave);

    // Cleanup: Cancel animation when component unmounts or correctState changes
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [correctState]); // Re-run when correctState changes to reset animation

  const chartData = {
    datasets: [
      {
        label: "EEG Signal (µV)",
        data: waveformData,
        borderColor: "#ffffff",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        type: "linear",
        position: "bottom",
        display: true,
        title: {
          display: true,
          text: "Time (seconds)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          font: {
            size: 10,
          },
          callback: function(value) {
            return `${value.toFixed(1)}s`;
          },
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        min: 0,
        max: SWEEP_DURATION_SECONDS,
      },
      y: {
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Amplitude (µV)",
          color: "#ffffff",
        },
        min: -120,
        max: 120,
        ticks: {
          color: "#ffffff",
          font: {
            size: 10,
          },
          stepSize: 120,
          callback: function(value) {
            if (value === 0) return "0";
            if (value === 120) return "120µV";
            if (value === -120) return "-120µV";
            return "";
          },
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  return (
    <MDBox>
      {/* Question Text */}
      <MDBox mb={3}>
        <MDTypography variant="h6" color="white" fontWeight="medium" mb={2}>
          {question}
        </MDTypography>
        
        {/* Answer Options */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          {isMultipleChoice && options ? (
            <FormGroup sx={{ gap: 1 }}>
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={selectedAnswer.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAnswer([...selectedAnswer, option.value]);
                        } else {
                          setSelectedAnswer(selectedAnswer.filter((val) => val !== option.value));
                        }
                      }}
                      sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        "&.Mui-checked": {
                          color: "#ffffff",
                        },
                      }}
                    />
                  }
                  label={
                    <MDTypography variant="body2" color="white">
                      {option.label}
                    </MDTypography>
                  }
                />
              ))}
            </FormGroup>
          ) : (
            <RadioGroup
              row
              value={selectedAnswer}
              onChange={(e) => setSelectedAnswer(e.target.value)}
              sx={{ gap: 2 }}
            >
              <FormControlLabel
                value="awake"
                control={
                  <Radio
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-checked": {
                        color: "#ffffff",
                      },
                    }}
                  />
                }
                label={
                  <MDTypography variant="body2" color="white">
                    Awake
                  </MDTypography>
                }
              />
              <FormControlLabel
                value="goestosleep"
                control={
                  <Radio
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-checked": {
                        color: "#ffffff",
                      },
                    }}
                  />
                }
                label={
                  <MDTypography variant="body2" color="white">
                    Goes to Sleep
                  </MDTypography>
                }
              />
              <FormControlLabel
                value="burstsupression"
                control={
                  <Radio
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-checked": {
                        color: "#ffffff",
                      },
                    }}
                  />
                }
                label={
                  <MDTypography variant="body2" color="white">
                    Burst Suppression
                  </MDTypography>
                }
              />
              <FormControlLabel
                value="eyeblink"
                control={
                  <Radio
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-checked": {
                        color: "#ffffff",
                      },
                    }}
                  />
                }
                label={
                  <MDTypography variant="body2" color="white">
                    Eye Blink
                  </MDTypography>
                }
              />
            </RadioGroup>
          )}
        </FormControl>
      </MDBox>

      {/* EEG Signal Graph */}
      <MDBox
        sx={{
          backgroundColor: deviceBackground,
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "4px",
          padding: "1rem",
          height: "300px",
          position: "relative",
        }}
      >
        <Line data={chartData} options={chartOptions} />
      </MDBox>
    </MDBox>
  );
}

export default EEGQuestion;
